import type { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import * as Sentry from '@sentry/react';
import { IS_DEVELOPMENT } from 'src/config';

export const defaultFeatureFlags = {
  EnableInviteWithRole:
    import.meta.env.PUBLIC_ENABLE_INVITE_WITH_ROLE === 'true',
  EnableEnterpriseDomainsPage:
    import.meta.env.PUBLIC_ENABLE_ENTERPRISE_DOMAINS_PAGE === 'true',
  EnableReactQueryDevtools:
    import.meta.env.PUBLIC_REACT_QUERY_DEVTOOLS === 'true',
  EnableUnoccupiedSeats:
    import.meta.env.PUBLIC_ENABLE_UNOCCUPIED_SEATS === 'true',
  EnableLinkToNewWebApp:
    import.meta.env.PUBLIC_ENABLE_LINK_TO_NEW_WEB_APP === 'true',
  EnableOffboardingTypeSelection:
    import.meta.env.PUBLIC_ENABLE_OFFBOARDING_TYPE_SELECTION === 'true',
  EnableLocalization: import.meta.env.PUBLIC_ENABLE_LOCALIZATION === 'true',
  EnableEnterpriseForceUnpairDevice:
    import.meta.env.PUBLIC_ENABLE_ENTERPRISE_FORCE_UNPAIR_DEVICE === 'true',
  EnableEnterpriseAnnualPlan:
    import.meta.env.PUBLIC_ENABLE_ENTERPRISE_ANNUAL_PLAN === 'true',
  EnableEnterpriseCommands:
    import.meta.env.PUBLIC_ENABLE_ENTERPRISE_COMMANDS === 'true',
  EnableEnterpriseSoftwareUpdatePolicy:
    import.meta.env.PUBLIC_ENABLE_ENTERPRISE_SOFTWARE_UPDATE_POLICY === 'true',
  EnableEnterpriseDirectDeleteUser:
    import.meta.env.PUBLIC_ENVIRONMENT !== 'production',
  // This is incredibly annoying during development so we only enable it in production.
  EnableEnterpriseInNewTab: import.meta.env.PUBLIC_ENVIRONMENT === 'production',
  UnitTestFlagDefaultFalse: false, // Used only for unit tests
  UnitTestFlagDefaultTrue: true, // Used only for unit tests
  // Stripe Sessions checkout with cloud-connect backend.
  // Existing checkout traffic is only rerouted when
  // EnableCheckoutV2EntrypointRedirect is on too.
  EnableCheckoutV2: true,
  // Shows the "I have a promo code" links that lead to Checkout V2. Only
  // effective together with EnableCheckoutV2 (see PromotionCheckoutLink).
  EnablePromoCodeLink: true,
  // Lets the connect payment-interval campaign (annual/biennial offers) be
  // previewed as active or inactive on demand, overriding the real campaign
  // dates.
  EnableConnectCampaign: IS_DEVELOPMENT,
  // Routes old checkout entry points to Checkout V2. EnableCheckoutV2 must
  // also be enabled for the new checkout to be accessible. Unlike
  // EnableCheckoutV2 (which only gates direct access to a new URL), this
  // flag actively reroutes existing checkout traffic, so it defaults to
  // off everywhere and is only turned on via an explicit LaunchDarkly
  // rollout.
  EnableCheckoutV2EntrypointRedirect: false,
};

export type FeatureFlag = keyof typeof defaultFeatureFlags;

export const mapLaunchDarklyFlagToFeatureFlag = (
  flagCollection: LDFlagSet | undefined = {}
) => {
  const flags: Partial<Record<FeatureFlag, boolean>> = {};

  if ('featureWebapp' in flagCollection) {
    const res = z.boolean().safeParse(flagCollection.featureWebapp);

    if (res.success) {
      flags.EnableLinkToNewWebApp = res.data;
    } else {
      Sentry.captureMessage(
        `LaunchDarkly flag 'featureWebapp' has invalid type (${typeof flagCollection.featureWebapp}), expected boolean. Falling back to default value: "${defaultFeatureFlags.EnableLinkToNewWebApp.toString()}"`
      );
    }
  }

  if ('enterpriseEnableAllCommands' in flagCollection) {
    const res = z
      .boolean()
      .safeParse(flagCollection.enterpriseEnableAllCommands);

    if (res.success) {
      flags.EnableEnterpriseCommands = res.data;
    } else {
      Sentry.captureMessage(
        `LaunchDarkly flag 'enterpriseEnableAllCommands' has invalid type (${typeof flagCollection.enterpriseEnableAllCommands}), expected boolean. Falling back to default value: "${defaultFeatureFlags.EnableEnterpriseCommands.toString()}"`
      );
    }
  }

  if ('enterpriseSoftwareUpdatePolicy' in flagCollection) {
    const res = z
      .boolean()
      .safeParse(flagCollection.enterpriseSoftwareUpdatePolicy);

    if (res.success) {
      flags.EnableEnterpriseSoftwareUpdatePolicy = res.data;
    } else {
      Sentry.captureMessage(
        `LaunchDarkly flag 'enterpriseSoftwareUpdatePolicy' has invalid type (${typeof flagCollection.enterpriseSoftwareUpdatePolicy}), expected boolean. Falling back to default value: "${defaultFeatureFlags.EnableEnterpriseSoftwareUpdatePolicy.toString()}"`
      );
    }
  }

  // LaunchDarkly assignment for routing old checkout entry points to Checkout V2.
  if ('checkoutV2EntrypointRedirect' in flagCollection) {
    const res = z
      .boolean()
      .safeParse(flagCollection.checkoutV2EntrypointRedirect);

    if (res.success) {
      flags.EnableCheckoutV2EntrypointRedirect = res.data;
    } else {
      Sentry.captureMessage(
        `LaunchDarkly flag 'checkoutV2EntrypointRedirect' has invalid type (${typeof flagCollection.checkoutV2EntrypointRedirect}), expected boolean. Falling back to default value: "${defaultFeatureFlags.EnableCheckoutV2EntrypointRedirect.toString()}"`
      );
    }
  }

  return flags;
};
